import { type SearchFilters, SearchResultsTab } from '../types/search';
import { safeSessionStorage } from './safeStorage';

type SearchStateInstance = {
	scrollPosition: number;
	numberOfItems: number;
};
const STORAGE_KEY = 'lastSearchState';

export type ResultSet = {
	mindItems?: SearchStateInstance;
	products?: SearchStateInstance;
	videoMindItems?: SearchStateInstance;
	courseMindItems?: SearchStateInstance;
	all?: SearchStateInstance;
};
type SearchState = ResultSet & {
	filters?: SearchFilters;
	courseFilters?: SearchFilters;
	tabKey?: SearchResultsTab;
	searchId?: string;
	searchSuggestionApplied?: boolean;
	firedSearchCompleted?: { searchId: string; tabs: string[] };
};

const searchStateKeyByTab: { [key in SearchResultsTab]: keyof ResultSet } = {
	[SearchResultsTab.MINDITEMS]: 'mindItems',
	[SearchResultsTab.PRODUCTS]: 'products',
	[SearchResultsTab.VIDEOS]: 'videoMindItems',
	[SearchResultsTab.COURSES]: 'courseMindItems',
	[SearchResultsTab.ALL]: 'all',
};

const getSearchStateKey = (tab: SearchResultsTab) => searchStateKeyByTab[tab];

const getSearchState: () => SearchState = () => {
	const searchState = safeSessionStorage.getItem(STORAGE_KEY);
	return searchState ? JSON.parse(searchState) : {};
};

const setSearchState = (searchState: SearchState) => {
	safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(searchState));
};

const getSearchFilters = () => {
	const searchState = getSearchState();
	return searchState.filters;
};

const getCourseSearchFilters = () => {
	const searchState = getSearchState();
	return searchState.courseFilters;
};

const getSearchId = () => {
	const searchState = getSearchState();
	return searchState.searchId;
};

const setSearchId = (searchId: string) => {
	const searchState = getSearchState();
	searchState.searchId = searchId;
	setSearchState(searchState);
};

const getSearchSuggestionApplied = () => {
	const searchState = getSearchState();
	return searchState.searchSuggestionApplied;
};

const setSearchSuggestionApplied = (value: boolean | undefined) => {
	const searchState = getSearchState();
	if (value === undefined) {
		delete searchState.searchSuggestionApplied;
	} else {
		searchState.searchSuggestionApplied = value;
	}
	setSearchState(searchState);
};

/**
 * Which tabs have already reported `Search Completed` for a given search journey.
 * Keyed by search id so a new journey starts empty without an explicit reset, and
 * session-scoped rather than mount-scoped so a reload — which deliberately keeps the
 * search id — cannot make an already-reported journey fire again.
 */
const hasFiredSearchCompleted = (searchId: string, tab: SearchResultsTab) => {
	const { firedSearchCompleted } = getSearchState();
	return firedSearchCompleted?.searchId === searchId && firedSearchCompleted.tabs.includes(tab);
};

const setFiredSearchCompleted = (fired: SearchState['firedSearchCompleted']) => {
	const searchState = getSearchState();
	searchState.firedSearchCompleted = fired;
	setSearchState(searchState);
};

const markSearchCompletedFired = (searchId: string, tab: SearchResultsTab) => {
	const fired = getSearchState().firedSearchCompleted;
	const tabs = fired?.searchId === searchId ? [...fired.tabs, tab] : [tab];
	setFiredSearchCompleted({ searchId, tabs });
};

const setSearchFilters = (filters: SearchFilters) => {
	const searchState = getSearchState();
	searchState.filters = filters;
	setSearchState(searchState);
};

const setCourseSearchFilters = (courseFilters: SearchFilters) => {
	const searchState = getSearchState();
	searchState.courseFilters = courseFilters;
	setSearchState(searchState);
};

const clearSearchState = (preserveJourneyFlags = false) => {
	const searchId = getSearchId();
	const firedSearchCompleted = getSearchState().firedSearchCompleted;
	const tabKey = getSearchTabKey();
	const searchSuggestionApplied = preserveJourneyFlags ? getSearchSuggestionApplied() : undefined;
	safeSessionStorage.removeItem(STORAGE_KEY);
	if (searchId) {
		// Preserve the active search journey ID when wiping session-scoped search state
		// so refinements (filters) keep using the same search_id until a new search starts.
		setSearchId(searchId);
		// The journey outlives the wipe, so which of its tabs already reported has to outlive
		// it too — otherwise the remount that follows reports them a second time.
		if (firedSearchCompleted) {
			setFiredSearchCompleted(firedSearchCompleted);
		}
	}
	if (tabKey) {
		// Preserve the active tab so breadcrumbs on product/video/reader pages link back to the correct search tab.
		setSearchTabKey(tabKey);
	}
	// Only the filter-reset path preserves journey-scoped flags so the downstream
	// SearchCompleted event still sees them. Reload/navigation must wipe them to
	// avoid misattributing later, unrelated searches. A new journey flag has to be
	// added to both halves of this preserve/restore pair or it is dropped here.
	if (searchSuggestionApplied !== undefined) {
		setSearchSuggestionApplied(searchSuggestionApplied);
	}
};

// A course-filter change is a new search for the courses list only: the library tabs keep
// their saved lists for the way back to /suche.
const clearCourseSearchState = () => {
	const searchState = getSearchState();
	delete searchState.courseMindItems;
	delete searchState.courseFilters;
	setSearchState(searchState);
};

const setSearchStateInstance = (
	keyofSearchState: keyof ResultSet,
	numberOfItems: number,
	scrollPosition: number
) => {
	const searchState = getSearchState();
	searchState[keyofSearchState] = { numberOfItems, scrollPosition };
	setSearchState(searchState);
};

const getSearchStateInstance = (keyofSearchState: keyof ResultSet) => {
	const searchState = getSearchState();
	return searchState[keyofSearchState];
};

const getSearchTabKey = (): SearchResultsTab | undefined => {
	const searchState = getSearchState();
	return searchState.tabKey;
};

const setSearchTabKey = (tabKey: SearchResultsTab) => {
	const searchState = getSearchState();
	searchState.tabKey = tabKey;
	setSearchState(searchState);
};

const searchResultsStorageManager = {
	getSearchState,
	setSearchState,
	getSearchFilters,
	getCourseSearchFilters,
	setSearchFilters,
	setCourseSearchFilters,
	clearSearchState,
	clearCourseSearchState,
	getSearchStateKey,
	getSearchStateInstance,
	setSearchStateInstance,
	getSearchTabKey,
	setSearchTabKey,
	getSearchId,
	setSearchId,
	getSearchSuggestionApplied,
	setSearchSuggestionApplied,
	hasFiredSearchCompleted,
	markSearchCompletedFired,
};

export default searchResultsStorageManager;
